<template>
  <v-container fluid class="pa-0">
    <v-card elevation="0" class="pa-4" tile>
      <v-row no-gutters align="center">
        <!-- Checkbox-based filter groups -->
        <v-col
          v-for="group in filterGroups"
          :key="group.key"
          cols="auto"
          class="mr-4"
        >
          <v-menu
            v-model="menuOpen[group.key]"
            :close-on-content-click="false"
            offset-y
            min-width="200"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="filters[group.key].length ? getSelectedLabels(group) : 'All'"
                :label="group.label"
                :append-icon="menuOpen[group.key] ? icons.menuUp : icons.menuDown"
                outlined
                dense
                hide-details
                readonly
                style="width: 150px;"
                v-bind="attrs"
                v-on="on"
                @click:append="menuOpen[group.key] = !menuOpen[group.key]"
              />
            </template>

            <v-card style="max-width: 280px; border-radius: 4px 4px 4px 4px;">
              <v-text-field
                v-if="group.options.length > searchThreshold"
                v-model="dropdownSearch[group.key]"
                placeholder="Search"
                :prepend-inner-icon="icons.mdiMagnify"
                outlined
                dense
                hide-details
                clearable
                class="mx-2 mt-1 mb-0"
                @click.stop
              />
              <div style="max-height: 300px; overflow-y: auto;">
                <v-list dense>
                  <v-list-item v-if="!filteredOptions(group).length">
                    <v-list-item-title class="grey--text caption">No options available</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-for="opt in filteredOptions(group)"
                    :key="optValue(opt)"
                    @click.stop="toggleFilter(group.key, opt)"
                  >
                    <v-list-item-action class="mr-2 my-1">
                      <v-checkbox
                        :input-value="isSelected(group.key, opt)"
                        dense
                        hide-details
                        @click.stop="toggleFilter(group.key, opt)"
                      />
                    </v-list-item-action>
                    <v-list-item-title>{{ optLabel(opt) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
              <template v-if="filters[group.key].length">
                <v-divider />
                <v-card-actions>
                  <v-btn text x-small color="grey" @click="clearFilter(group.key)">Clear</v-btn>
                </v-card-actions>
              </template>
            </v-card>
          </v-menu>
        </v-col>

        <v-spacer />

        <!-- Search -->
        <v-col cols="auto">
          <v-text-field
            :value="search"
            @input="$emit('update:search', $event)"
            label="Search..."
            :append-icon="icons.mdiMagnify"
            outlined
            dense
            hide-details
            style="width: 250px;"
          />
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { mdiMenuDown, mdiMenuUp, mdiMagnify } from '@mdi/js';

export default {
  name: 'PeopleFilters',
  props: {
    filters: {
      type: Object,
      required: true,
    },
    search: {
      type: String,
      required: true,
    },
    // Default groups used by Operators page
    statusOptions:   { type: Array, default: () => ['Enabled', 'Disabled'] },
    tagOptions:      { type: Array, default: () => [] },
    groupOptions:    { type: Array, default: () => [] },
    locationOptions: { type: Array, default: () => [] },
    // Override: pass a fully custom array of { key, label, options }
    // When provided this replaces all default groups
    filterGroupsConfig: { type: Array, default: null },
  },
  data() {
    return {
      menuOpen: {},
      dropdownSearch: {},
      searchThreshold: 5,
      icons: {
        menuDown: mdiMenuDown,
        menuUp: mdiMenuUp,
        mdiMagnify: mdiMagnify,
      },
    };
  },
  computed: {
    filterGroups() {
      if (this.filterGroupsConfig) return this.filterGroupsConfig;
      return [
        { key: 'status',    label: 'Status',   options: this.statusOptions },
        { key: 'tags',      label: 'Tag',      options: this.tagOptions },
        { key: 'groups',    label: 'Group',    options: this.groupOptions },
        { key: 'locations', label: 'Location', options: this.locationOptions },
      ];
    },
  },
  created() {
    this.filterGroups.forEach(g => {
      this.$set(this.menuOpen, g.key, false);
      this.$set(this.dropdownSearch, g.key, '');
    });
  },
  watch: {
    menuOpen: {
      deep: true,
      handler(val) {
        Object.keys(val).forEach(key => {
          if (!val[key]) this.$set(this.dropdownSearch, key, '');
        });
      },
    },
  },
  methods: {
    filteredOptions(group) {
      const q = (this.dropdownSearch[group.key] || '').toLowerCase().trim();
      if (!q) return group.options;
      return group.options.filter(opt =>
        this.optLabel(opt).toLowerCase().includes(q)
      );
    },
    // Support both plain strings and { text, value } option objects
    optValue(opt) { return opt?.value ?? opt; },
    optLabel(opt) { return opt?.text ?? opt; },
    getSelectedLabels(group) {
      const selected = this.filters[group.key] || [];
      return selected.map(val => {
        const found = group.options.find(o => (o?.value ?? o) === val);
        return found ? (found?.text ?? found) : val;
      }).join(', ');
    },
    isSelected(key, opt) {
      return (this.filters[key] || []).includes(this.optValue(opt));
    },
    toggleFilter(key, opt) {
      const val = this.optValue(opt);
      const current = [...(this.filters[key] || [])];
      const idx = current.indexOf(val);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(val);
      this.$emit('update:filters', { ...this.filters, [key]: current });
    },
    clearFilter(key) {
      this.$emit('update:filters', { ...this.filters, [key]: [] });
    },
  },
};
</script>